import React, { useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MailCheck, ArrowLeft, KeyRound } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      setSent(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-secondary/40">
      <Card className="w-full max-w-md p-7 shadow-premium" data-testid="forgot-password-card">
        {sent ? (
          <div className="text-center">
            <span className="icon-chip bg-emerald-500/12 text-emerald-700 mx-auto mb-4">
              <MailCheck className="h-5 w-5" />
            </span>
            <h1 className="text-xl font-semibold mb-2">Check your inbox</h1>
            <p className="text-sm text-muted-foreground leading-relaxed" data-testid="forgot-password-sent">
              If <span className="font-medium text-foreground">{email}</span> has a Tharwy account, a
              reset link is on its way. It works once and expires in an hour.
            </p>
            <Link to="/login">
              <Button variant="secondary" className="mt-6 w-full" data-testid="forgot-back-to-login">
                Back to sign in
              </Button>
            </Link>
          </div>
        ) : (
          <>
            <span className="icon-chip bg-emerald-500/12 text-emerald-700 mb-4">
              <KeyRound className="h-5 w-5" />
            </span>
            <h1 className="text-xl font-semibold">Forgotten your password?</h1>
            <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">
              Enter the email on your account and we'll send you a link to set a new password.
              No need to contact support.
            </p>
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div>
                <Label htmlFor="fp-email">Email</Label>
                <Input id="fp-email" type="email" required value={email} autoComplete="email"
                  onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com"
                  className="mt-1.5" data-testid="forgot-password-email" />
              </div>
              {error && <p className="text-sm text-red-500" data-testid="forgot-password-error">{error}</p>}
              <Button type="submit" className="w-full" disabled={busy || !email.trim()}
                data-testid="forgot-password-submit">
                {busy ? "Sending…" : "Send reset link"}
              </Button>
            </form>
            <Link to="/login"
              className="mt-5 inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground">
              <ArrowLeft className="h-3.5 w-3.5" /> Back to sign in
            </Link>
          </>
        )}
      </Card>
    </div>
  );
}
